import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Container,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import SelectOrganizationStatus from "components/SelectOrganizationStatus";
import SelectOrganizationType from "components/SelectOrganizationType";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  AdminListRequestOfOrganizationFilter,
  ListResponseOfOrganizationModel,
  OrganizationFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import OrganizationStatusChip from "./OrganizationStatusChip";

const OrganizationList = () => {
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const {
    organizationService,
    organizationCreateUrl,
    organizationDetailsUrl,
  } = useGlobalContext();
  const { organizationId } = contextStore.useState();

  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const searchTerm = persistanceStore.useState(
    (s) => s.organizationFilterSearchTerm
  );
  const statusId = persistanceStore.useState(
    (s) => s.organizationFilterStatusId
  );
  const typeId = persistanceStore.useState((s) => s.organizationFilterTypeId);
  const debouncedSearchTerm = useDebounce(searchTerm?.trim(), 500);

  useListTitle("organizations");

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfOrganizationModel>(
    [
      "organizations",
      organizationId,
      page,
      rowsPerPage,
      orderBy,
      order,
      debouncedSearchTerm,
      typeId,
      statusId,
    ],
    () =>
      organizationService.getOrganizations(
        AdminListRequestOfOrganizationFilter.fromJS({
          organizationId,
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: OrganizationFilter.fromJS({
            searchText: debouncedSearchTerm,
            organizationTypeIds: !typeId ? undefined : [typeId],
            organizationStatusIds: !statusId ? undefined : [statusId],
          }),
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, typeId, statusId]);

  const organizations =
    data?.data?.map((x) => ({
      ...x,
      id: x.id,
    })) ?? [];

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      type: "text",
      customSortAccessor: "name",
      label: t({ id: "nameLabel" }),
    },
    {
      accessor: "parentName",
      align: "left",
      noSort: true,
      type: "text",
      label: t({ id: "parentOrganizationLabel" }),
    },
    {
      accessor: "organizationStatusName",
      align: "left",
      type: "node",
      customSortAccessor: "organizationStatus.name",
      label: t({ id: "statusLabel" }),
      View: OrganizationStatusChip,
    },
    {
      accessor: "organizationTypeName",
      align: "left",
      type: "text",
      customSortAccessor: "organizationType.name",
      label: t({ id: "typeLabel" }),
    },
  ];

  const onClick = (id: string) => {
    navigate(organizationDetailsUrl(id));
  };

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  return (
    <Container>
      <Box mb={3} justifyContent="center" display="flex">
        <Link to={organizationCreateUrl()} style={{ textDecoration: "none" }}>
          <Fab aria-label="add" color="primary" data-cy="addButton">
            <AddIcon />
          </Fab>
        </Link>
      </Box>

      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              value={searchTerm ?? ""}
              placeholder={t({ id: "searchPlaceholder" })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        persistanceStore.update((s) => {
                          s.organizationFilterSearchTerm = "";
                        })
                      }
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e) =>
                persistanceStore.update((s) => {
                  s.organizationFilterSearchTerm = e.target.value;
                })
              }
              data-cy="searchText"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <SelectOrganizationStatus
              label={t({ id: "statusLabel" })}
              textFieldProps={{ variant: "outlined" }}
              value={statusId}
              onChange={(id) =>
                persistanceStore.update((s) => {
                  s.organizationFilterStatusId = id;
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <SelectOrganizationType
              label={t({ id: "typeLabel" })}
              textFieldProps={{ variant: "outlined" }}
              value={typeId}
              onChange={(id) =>
                persistanceStore.update((s) => {
                  s.organizationFilterTypeId = id;
                })
              }
            />
          </Grid>
        </Grid>
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: organizations ?? [],
          loading: isFetching,
          dense: true,
          title: t({ id: "organizations" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => organizationDetailsUrl(id)),
          ],
        }}
      />
    </Container>
  );
};

export default OrganizationList;
